import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Coordena o scroll do app a cada mudança de rota.
 *
 * Comportamento:
 * - Sem `hash`: rola para o topo (mudança de página normal).
 * - Com `hash` (ex.: `/#servicos`, `/sobre-nos#fundador`): rola até o
 *   elemento de mesmo `id`. Usa `requestAnimationFrame` para esperar o
 *   conteúdo da nova rota montar antes de procurar o elemento.
 *
 * Também desabilita a `history.scrollRestoration` do navegador para que
 * voltar/avançar não tente restaurar a posição automaticamente — caso
 * contrário, a restoration do navegador compete com o nosso scrollTo e
 * faz o usuário aterrissar no meio de uma página.
 *
 * Precisa viver dentro do `<BrowserRouter>` e fora das `<Routes>`.
 */
export function ScrollManager() {
  const { pathname, hash, key } = useLocation();

  // Garante que o app controla o scroll, não o navegador.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (!hash) {
      // `window.scrollTo(x, y)` (forma posicional, sem options) é SEMPRE instant
      // e ignora o CSS `html { scroll-behavior: smooth }` definido em index.css.
      // A forma com options (`scrollTo({behavior:'instant'})`) é interpretada de
      // forma inconsistente: alguns browsers caem para 'auto', que herda o CSS
      // smooth e fica em um scroll animado de 1s — durante esse tempo a nova
      // rota renderiza e o scroll fica preso no meio.
      //
      // Dois rAFs garantem que rodamos depois do layout+paint da nova rota.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      });
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // O conteúdo lazy pode ainda não ter montado; tenta de novo até ~10 frames.
      if (attempts < 10) {
        attempts += 1;
        requestAnimationFrame(tryScroll);
      } else {
        // fallback se o id não existe na rota
        window.scrollTo(0, 0);
      }
    };

    requestAnimationFrame(tryScroll);
    // `key` muda mesmo quando navegamos para a mesma rota+hash; útil para repetir o
    // scroll caso o usuário clique de novo na mesma âncora.
  }, [pathname, hash, key]);

  return null;
}
